/**
 * The imports below need to be kept as a package references. Changing them to local references
 * will lead to build error in COP with "Binding element 'value' implicitly has an 'any' type.".
 * There is probably a circular reference in shared causing this.
 */
import * as dynamicConfiguration from './dynamic-configuration';
import { IMoney } from '../lib/money';
import { SubPayload } from '../apollo/types';
import { IBuiltBet } from '../components/bet-builder/types';
import { VAPIMultipleBetLeg } from '../betslip/get-bet-opportunities';
import { Tab } from '../components/event/tabbed-event/types';
import { MarketDisplayGroup } from '../lib/events-with-key-market-groups';

export { dynamicConfiguration };
export * from './venue-api';
import type { PlayerCardOperator as PCO } from '../components/player-card/state/types';

export type PlayerCardOperator = PCO;

/**
 * @deprecated Use `| null` directly
 */
export type MaybeNull<T> = T | null;

export type Subset<T, U extends T> = U;

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IAppContext {}

export interface DeviceAddress {
  line1: string;
  line2: string;
}

export interface IDeviceAttributes {
  locked: boolean;
  disabled: boolean;
  deviceId: string;
  address?: DeviceAddress | null;
}

export interface IDevice {
  acceptedTender: string;
  balances: IBalance[];
  currency: string;
  deviceId: string;
  accountId: string;
  label: string;
  qtyToday: number;
  deviceAttributes: IDeviceAttributes | null;
  slotNumber: number;
  type: string;
  category?: string | null;
  qtyTodayLabel: string;
  isMyDevice: boolean;
  externalReference?: string | null;
  // TODO: Should be removed as part of ROSI-16842
  unreachable?: boolean;
  cashAccepted?: ICashAccepted;
}

export interface IGraphQlUrls {
  http: string;
  ws?: string;
}

export interface IConfig {
  APP?: string;
  DEFAULT_LOCALE?: string;
  DEFAULT_MESSAGE_TIMEOUT?: number;
  DEFAULT_ODDS_FORMAT?: string;
  URLS: {
    contentDelivery: Record<'base' | 'venueInfo', string>;
    graphql: IGraphQlUrls;
    stomp?: string;
    terminal?: {
      base: string;
      environmentInfo: string;
      deviceInfo: string;
      printBetReceipt: string;
      printWalletReceipt: string;
      printRoundReceipt?: string;
      printContestEntry?: string;
      printReportableWinnings?: string;
      printCompSlip?: string;
      deviceToken: string;
      balanceChangeAck?: string;
    };
    parimutuel?: {
      betdirector: string;
      proxy: string;
    };
    ppm?: string;
    tim?: {
      base: string;
      deviceActivity: string;
    };
  };
  RESET_TIMEOUT_SECONDS?: number;
  RESET_MODAL_COUNTDOWN_SECONDS?: number;
  RACEBOOK_ONLY_RESET_TIMEOUT_SECONDS?: number;
  BET_REFERRAL_TIMEOUT_SECONDS?: number;
  BET_REFERRAL_OFFER_TIMEOUT_SECONDS?: number;
  BET_REFERRAL_ADDITIONAL_TIMEOUT_SECONDS?: number;
  ENABLE_FULLSTORY: boolean;
  FETCH_REWARD_POINTS_FROM_BE?: boolean;
  GTMID: string;
  STATE?: StateCode;
  STOMP_HOST?: string;
  STOMP_LOGIN?: string;
  TRACKINGID: string;
  TRANSACTION_PAGE_SIZE?: number;
  WH_CLUSTER: string;
  WH_NAMESPACE: string;
  support: Record<string, (args?: any[]) => any>;
  SGP_FEATURE: boolean;
  NRMD_ENABLED?: boolean;
  APOLLO_WS_MIN_TIMEOUT?: number;
}

export interface IBalance {
  amount: number;
  currency: string;
  type: BalanceType;
  change?: number;
  businessActivityType?: string;
}

export enum BalanceType {
  CASH = 'CASH',
  PLAYABLE = 'PLAYABLE',
  PRIMARY = 'PRIMARY',
  TOTAL = 'TOTAL'
}

export interface IParseBetPlacementSpec {
  messageType: string;
  payload: any;
  clearSelections(): void;
}

export interface IAccount {
  __typename?: 'Account';
  id: string;
  type: AccountType;
  category?: string | null;
  balances: IBalance[];
  slotNumber: number;
  transferTargets: string[];
  deviceAttributes: IDeviceAttributes | null;
  externalReference: string | null;
  subtotal: {
    sessionId: string;
    sold: number;
    paid: number;
    amount: number;
    credits: number;
  } | null;
}

export interface ITrendingBet {
  id: string;
  betCount: number;
  price: ITrendingBetPrice;
  legs: ITrendingBetLeg[];
}

export interface ITrendingBetLeg {
  eventId: string;
  marketId: string;
  selectionId: string;
  event: IEvent;
}

export interface ITrendingBetPrice {
  decimal: number;
  fractional: string;
  american: string;
}

export interface IEventSelectionMarket {
  displayed: string;
  hcapValue: number | null;
  id: string;
  name: string;
  status: string;
}

export interface ILegInput {
  eventId: string;
  line?: number | null;
  marketId: string;
  selectionId: string;
  priceType: string;
  price: IPriceInput;
  originalPrice?: IPriceInput;
  stakePerLine: number;
  parlayStarred?: boolean;
  requestedLine?: number | null;
  rewardCreditsPerLine?: number;
  rewardCreditsStakePerLine?: number;
}

export type ILegInputV2 = Omit<ILegInput, 'price' | 'originalPrice'> & {
  price: IPriceInputV2;
  originalPrice?: IPriceInputV2;
  included: boolean;
};

export interface IEventSelectionMarket {
  displayed: string;
  hcapValue: number | null;
  id: string;
  name: string;
  status: string;
}

export interface IEventSelection {
  ancestors: any;
  currentPriceDen: string;
  currentPriceNum: string;
  displayed: string;
  event: Pick<IEvent, 'displayed' | 'id' | 'name' | 'status'>;
  fbResult: string;
  id: string;
  market: IEventSelectionMarket[];
  name: string;
  status: string;
}

export interface ISelectedSport {
  competitions: Pick<ICompetition, 'id' | 'name'>[];
  id: string;
  name: string;
  primary: boolean;
  primaryMarkets: IMarketList;
}

export interface ILegCompetition {
  id: string;
  name: string;
}

export interface ILegEvent {
  raceNumber: number | null;
  id: string;
  name: string;
}

export interface ILegMarket {
  id: string;
  name: string;
}

export interface ILegResult {
  type: ResultType;
}

export interface ILegSelectionRunner {
  number: string | undefined;
  name: string;
}

export interface ILegSelection {
  id: string;
  name: string;
  runners: ILegSelectionRunner[] | null;
}

export interface ILegSport {
  id: string;
  name: string;
}

export interface ILeg {
  competition: ILegCompetition;
  event: ILegEvent;
  id: string;
  type: string | null;
  market: ILegMarket;
  marketLine: number;
  price: IPriceInput | null;
  originalPrice: IPriceInput | null;
  priceType: string;
  result: ILegResult | null;
  selection: ILegSelection;
  sport: ILegSport;
  eventStartTime: string;
}

export interface IBetLeg {
  type: string;
  typeName?: string;
  legRefs: string[];
  price: IPriceInput;
  originalPrice: IPriceInput | null;
}

export type IPriceInput = {
  d: number;
  a?: string;
  f: string;
};

export type IPriceInputV2 = {
  decimal: number;
  american?: string;
  fractional: string;
};

export interface IBetWithError {
  selectionId: string;
  marketId: string;
  eventId: string;
  type?: string;
}

export type IPrice = {
  decimal: number;
  fractional: string | null;
  american: string | null;
};

export interface ICanPayoutBet {
  pos: boolean;
  kiosk: boolean;
  cop: boolean;
}

// As it's received from VAPI before we mutate it : (
export interface IPlacedBet {
  id: string;
  type: string;
  typeName?: string;
  numLines?: number;
  status: string;
  placedAt: string;
  placedBy: { id: string };
  currency: string;
  totalStake: number;
  stakePerLine?: number;
  placedAtLocation?: { venueId: string; deviceType: string; slotNumber: number };
  potentialReturns: number;
  payout: number | null;
  payoutExpiresAt: string | null;
  payOutType: string;
  paidOut: boolean;
  settled: boolean;
  price: IPriceInput | null;
  estimatedOdds: IPriceInput | null;
  originalPrice: IPriceInput | null;
  retailTicketId: string;
  playerCardId: string | null;
  blockPayout: boolean;
  betLegs: IBetLeg[];
  legs: ILeg[];
  extensions?: IBetExtensions;
  boxed: boolean | null;
  cashOut: boolean | null;
  showVoidedAdjustmentMessage: boolean;
}

export interface IBet extends Omit<IPlacedBet, 'totalStake' | 'potentialReturns' | 'payout'> {
  totalStake: IMoney;
  potentialReturns: IMoney;
  originalPotentialReturns?: IMoney;
  toWin: IMoney;
  originalToWin?: IMoney;
  payout: IMoney;
  betStatus: BetStatusType;
  canPayout: ICanPayoutBet;
  canVoid: boolean;
  canCashOut: boolean;
  rewardCreditsPerLine: number | null;
}

export type IBetExtensions = {
  money: BetExtensionOfMoney | null;
  reprints: IPrint[];
  cashOut: CashOutExtension;
} & AppliedTaxesExtension;

export type BetExtensionOfMoney = {
  questions?: Record<'id' | 'text' | 'title', string>[];
  taxes?: BetTax[];
  type: string;
};

export type AppliedTaxesAmountExtensions = {
  money: {
    taxes?: {
      applied: {
        amount: number;
      } | null;
    }[];
  };
};

export type AppliedTaxesExtension = {
  money: {
    taxes?: {
      applied: {
        rate: number;
      } | null;
    }[];
  };
} & AppliedTaxesAmountExtensions;

export type BetTax = {
  questionId?: string;
  taxType: string;
} & Partial<
  Record<
    'no' | 'yes',
    {
      amount: number;
      rate: number;
    } | null
  >
>;

export type CombinedTaxes = BetTax & Partial<Required<AppliedTaxesExtension['money']>['taxes'][0]>;

export interface IBettingError {
  code: string;
  message?: string;
  pointer: string | null;
  details?: {
    message?: string;
  } | null;
  field?: string | null;
  resource: string | null;
}

export interface IParlayCardBettingError extends IBettingError {
  details: {
    severity: 'INFO' | 'WARNING' | 'ERROR';
    message?: string;
  } | null;
}

export interface ISingle {
  id: string;
  marketId: string;
  eventId: string;
  eachWay: boolean;
  type: string;
  stakePerLine: number;
  potentialReturns: number;
  numOfLines: number;
  priceType: string;
  price: IPrice | null;
  buyingPointsAllowed: boolean;
  betWithRewardCreditsAllowed: boolean;
  // No longer returned by Sportsbook API (and therefore Venue API)
  errors?: IBettingError[] | null;

  /**
   * Field available with v2 betting ops call
   */
  bettable?: boolean;
}

// None of these fields are returned in the BETTING_OPPORTUNITIES or GET_BUILT_BET Venue API queries
// Do not add anything to this interface that is returned by these queries... add to ISingle instead
export interface IPossibleSingle extends ISingle {
  channel?: string;
  country?: string;
  currency?: string;
  line?: string;
  selectionId?: string;
  startDateTime?: string;
  eventIsInPlay?: boolean;
  hasBetReferral?: boolean;
  delay?: number;
  status?: string;
  displayed?: string;
  originalPrice?: IPrice | null;
}

export interface IMultipleLeg {
  selectionId: string;

  /**
   * Field available with v2 betting ops call
   */
  included?: boolean;
}

export interface IMultiple {
  eachWay: boolean;
  id: string;
  potentialReturns: number;
  stakePerLine: number;
  type: string;
  numOfLines: number;
  typeName: string;
  price: IPrice | null;
  betWithRewardCreditsAllowed: boolean;
  legs?: IMultipleLeg[]; // legs field isn't requested (in BETTING_OPPORTUNITIES_QUERY and GET_BUILT_BET) for RoundRobin
  errors?: IBettingError[] | null; // No longer returned by Sportsbook API (and therefore Venue API)
  isSuperParlay?: boolean;
  betLegs?: VAPIMultipleBetLeg[] | null;

  /**
   * Field available with v2 betting ops call
   */
  bettable?: boolean;
}

export interface IParlay extends IMultiple {
  legs: IMultipleLeg[];
  betLegs: VAPIMultipleBetLeg[] | null;
}
export const isParlay = (multiple: IMultiple): multiple is IParlay => !!multiple.legs;

export interface ISuperParlay extends IParlay {
  betLegs: VAPIMultipleBetLeg[] | null;
}

export interface IRoundRobin {
  eachWay: boolean;
  id: string;
  potentialReturns: number;
  stakePerLine: number;
  type: string;
  numOfLines: number;
  typeName: string;
  price: IPrice | null;
  betWithRewardCreditsAllowed: boolean;
}

type SkeletalTeaser<T> = T extends undefined ? {} : { type: T };

export interface IAvailableTeaser extends SkeletalTeaser<string> {
  __typename?: string;
  type: string;
  eachWay: boolean;
  name: string | null;
  levels: ITeaserLevel[];
  teaserSelections: string[];
  stakePerLine: number;
  price: IPrice | null;
  teaserLevel: number | null;
  potentialReturns: number;
  betWithRewardCreditsAllowed: boolean;
  bettable?: boolean;
}

export interface INullTeaser extends SkeletalTeaser<null> {
  __typename?: string;
  type: null;
  eachWay: false;
  name: null;
  teaserSelections: null;
  levels: null;
  teaserLevel: null;
  stakePerLine: null;
  price: null;
  potentialReturns: null;
  betWithRewardCreditsAllowed: null | false;
}

export interface IEmptyTeaser extends SkeletalTeaser<undefined> {
  __typename?: string;
  eachWay: false;
  betWithRewardCreditsAllowed: false;
}

export type IUnavailableTeaser = INullTeaser | IEmptyTeaser;
export type ITeaser = IAvailableTeaser | IUnavailableTeaser;

export const isTeaserAvailable = (teaser: ITeaser): teaser is IAvailableTeaser => 'type' in teaser && !!teaser.type;

export interface ITeaserLevel {
  level: number;
  points: number;
}

export interface IStake {
  /** Text that can be entered into the keypad after appends whats stored in value, but can't represented its type money/number e.g. '.', '.0', '.10'. */
  incompleteFractionSuffix?: string;
  value: IMoney;
  changedFromResponse?: boolean;
}

export interface IStakesValue {
  wager: IStake;
  potentialReturns: IStake;
  /** Toggled to true when the user selects bet with credits. */
  withCredits: boolean;
}

export interface IStakes {
  /** Either the selection id when a straight/single or the bet type (e.g., `DBL`, `TSR2`, `2F3`, etc). */
  [key: string]: IStakesValue;
}

export interface IParsedLeg {
  eventId: string;
  marketId: string;
  selectionId: string;
  line: number | null;
  price: {
    f: string;
  };
  originalPrice?: {
    f: string;
  };
  parlayStarred?: boolean;
  priceType: string;
  type: string;
}

export interface IParsedBet {
  channel: string;
  eachWay: boolean;
  legs: IParsedLeg[];
  stakePerLine: number;
  type: string;
  requestedPotentialReturns?: number;
  rewardCreditsPerLine?: number;
  rewardCreditsStakePerLine?: number;
  activityId?: string;
}

export interface IParsedSingleBets {
  bets: IParsedBet[];
  playerCardId?: string;
}

export interface IErrorDetails {
  code: string;
  exception: {
    code?: string;
    description?: string;
    title?: string;
    details: any;
  };
  title?: string;
  details?: any;
}

export interface IPlaceBetErrorHandlerProps {
  error: IErrorDetails;
  operationVariables?: any;
}

export enum BetslipViewType {
  SINGLES = 'singles',
  PARLAYS = 'parlays',
  ROUNDROBINS = 'roundrobins',
  BETAHEAD = 'betahead'
}

export interface ISportsData {
  id: string;
}

export type SportsDataMessageKind = 'event' | 'market' | 'selection';

export interface ISport extends ISportsData {
  name: string;
  displayOrder: number;
  primary: boolean;
  newUntil: string | null;
  primaryMarkets: IMarketList;
  sportPagesMarketCategoryTemplates: SportPagesMarketCategoryTemplates;
}

export interface ICompetition extends ISportsData {
  __typename?: string;
  name: string;
  cmsGroupDisplayOrder: number;
  cmsDisplayOrder: number;
  events: IEvent[];
}

export type ICompetitionWithEventKeyMarketGroups = {
  id: string;
  name: string;
  displayAsEvent: boolean;
  cmsGroupDisplayOrder: number;
  cmsDisplayOrder: number;
  events: IEventWithKeyMarketGroups[];
};

export type EventType = 'MATCH' | 'TNMT' | 'BAND01' | 'BAND02' | 'BAND03' | 'BAND04' | 'BAND05' | 'BAND06';

export interface IEvent extends ISportsData {
  __typename?: string;
  name: string;
  competitionId: string;
  competitionName: string;
  competitionDisplayOrder: number;
  ancestors: string[];
  displayed: string;
  isInPlay: string;
  isBoosted: boolean;
  marketField: IMarket[];
  startDateTime: string;
  status: string;
  teams: ITeam[];
  metadata: IEventMetadata | null;
  type: EventType;
}

export interface IEventWithKeyMarketGroups extends IEvent {
  keyMarketGroups: MarketDisplayGroup[];
  sportId?: string;
  eventDisplay: {
    showEventHeader: boolean;
  };
  tabs: Tab[];
  secondaryTabs: Tab[];
}

export interface IEventMetadata {
  __typename?: string;
  currentHomeStartingPitcher: string | null;
  currentAwayStartingPitcher: string | null;
}

export interface IMarket extends ISportsData {
  __typename?: string;
  name: string;
  marketCode?: string | null;
  byoEligible?: string;
  ancestors: string[];
  constextId?: string;
  displayed: string;
  displayOrder?: number;
  hcapValue: number | null;
  status: string;
  collectionDisplayOrder: number;
  marketTemplateDisplayOrder: number;
  selections: ISelection[];
  parentId?: string | null;
  metadata?: any;
  templateName?: string;
  specialConditions?: string;
  movingLines?: IMovingLines | null;
}

export interface IMovingLines {
  status: string;
  displayed: boolean;
  cashOutAvailable: boolean;
  minPricedLine: number;
  maxPricedLine: number;
  lineIncrement: number;
  linePrices: IMovingLine[];
}

export interface IMovingLine {
  line: number;
  displayed: boolean;
  status: string;
  selections: IMovingLineSelection[];
}

export interface IMovingLineSelection {
  selectionType: string;
  currentPriceNum: string;
  currentPriceDen: string;
}

export type BetslipSelection = ISelection & {
  eventNameOverride?: string;
  footer?: JSX.Element;
  marketNameOverride?: string;
  nameOverride?: JSX.Element;
};

/**
 * The below boosted types are the response types. Before being decorated
 */
export type BoostedSelection = Pick<
  ISelection,
  | '__typename'
  | 'ancestors'
  | 'currentPriceNum'
  | 'currentPriceDen'
  | 'currentPriceDecimal'
  | 'displayed'
  | 'fbResult'
  | 'id'
  | 'name'
  | 'status'
  | 'metadata'
>;

export type BoostedEvent = Pick<
  IEvent,
  | 'id'
  | 'name'
  | 'competitionId'
  | 'competitionName'
  | 'ancestors'
  | 'displayed'
  | 'isInPlay'
  | 'startDateTime'
  | 'status'
  | 'teams'
  | 'metadata'
> & {
  marketField: BoostedMarket[];
  __typename?: string;
};

export type BoostedMarket = Pick<
  IMarket,
  | '__typename'
  | 'id'
  | 'name'
  | 'byoEligible'
  | 'ancestors'
  | 'displayed'
  | 'hcapValue'
  | 'status'
  | 'parentId'
  | 'marketCode'
> & {
  selections: BoostedSelection[];
  betLimits?: {
    __typename?: string;
    maxBet?: number;
  };
};

export type BoostedCompetition = Pick<
  ICompetition,
  '__typename' | 'id' | 'name' | 'cmsGroupDisplayOrder' | 'cmsDisplayOrder'
> & {
  events: BoostedEvent[];
};

export type BoostedEventsResponse = { boostedEventsByCompetition: BoostedCompetition[] | null };

export type DecoratedBoostedCompetition = Omit<BoostedCompetition, 'events'> & { events: DecoratedBoostedEvent[] };

export type DecoratedBoostedSelection = BoostedSelection & {
  event: ISelectionEvent;
  market: ISelectionMarket;
  sport: string;
  hcapValue?: number;
  isDisplayed: boolean;
  isSuspended: boolean;
  marketLine: number | null;
  requestedLine: null;
};

export type DecoratedBoostedMarket = Omit<BoostedMarket, 'selections'> & {
  selections: DecoratedBoostedSelection[];
};

export type DecoratedBoostedEvent = Omit<BoostedEvent, 'marketField'> & {
  marketField: DecoratedBoostedMarket[];
};

export type BetAttributionState = Record<string, string | null>;

export interface ISelection extends ISportsData {
  __typename?: string;
  id: string;
  name: string;
  ancestors: string[];
  biNumber?: string;
  event: ISelectionEvent;
  market: ISelectionMarket;
  isSuspended: boolean;
  isDisplayed: boolean;
  status: string;
  displayed: string;
  currentPriceDen: string;
  currentPriceNum: string;
  currentPriceDecimal: number;
  marketLine: number | null;
  requestedLine: number | null;
  hcapValue?: number | null;
  potentialReturns?: IMoney;
  fbResult: string;
  sport?: string;
  starred?: boolean;
  withParlayBets?: boolean;
  betAttributions?: BetAttributionState;
  searchMetadata?: {
    queryId: string;
    objectId: string;
  };
  buyingPointsAllowed?: boolean;
  betWithRewardCreditsAllowed?: boolean;
  cannedParlay?: { id: string; selectionIds: string[] };
  originalPrice?: IPrice;
  metadata?: ISelectionMetadata;
}

export interface ISelectionMetadata {
  __typename?: string;
  team?: 'AWAY' | 'HOME' | null;
  player?: string | null;
}

export interface ISelectionEvent {
  id: string;
  name: string;
  isSuspended: boolean | string;
  isDisplayed: boolean;
  status: string;
  displayed: string;
  startDateTime?: string;
  isInPlay: string;
  isBoosted: boolean;
  competitionName?: string;
  competitionId?: string;
}

export interface ISelectionMarket {
  id: string;
  name: string;
  isSuspended: boolean | string;
  isDisplayed: boolean;
  status: string;
  displayed: string;
  hcapValue: MaybeNull<number>;
  movingLines?: IMovingLines | null;
  marketCode?: string;

  betLimits?: {
    maxBet?: number;
  };
}

export interface ISelectionUpdate {
  id: string;
  ancestors: string[];
  currentPriceDen: string | null;
  currentPriceNum: string | null;
  status: string | null;
  displayed: string | null;
  hcapValue: number | null;
  isInPlay?: string | null;
  movingLines?: IMovingLines | null;
}

export interface ITeam {
  __typename?: string;
  name: string;
  fbResult: string;
}

export type SportPagesMarketCategoryTemplates = {
  eventPage: SportPagesMarketCategoryTemplatesEventPage;
  schedulePage: SportPagesMarketCategoryTemplatesSchedulePage;
};

type SportPagesMarketCategoryTemplatesEventPage = {
  marketCategoriesToTemplates: Record<string, MarketTemplates | SixPackMarketTemplates | undefined>;
};

type SportPagesMarketCategoryTemplatesSchedulePage = {};

export interface IMarketList {
  preMatch: string[];
  inPlay: string[];
}

export enum Brand {
  WilliamHill = 'WILLIAM_HILL',
  Caesars = 'CAESARS',
  GENERIC = 'GENERIC'
}

export enum MarketTemplates {
  Accordion = 'ACCORDION',
  MarketsByTeam = 'MARKETS_BY_TEAM',
  MarketsByOverUnder = 'MARKETS_BY_OVER_UNDER',
  PlayerOverUnder = 'PLAYER_OVER_UNDER',
  PlayerYesNo = 'PLAYER_YES_NO',
  FirstLastAnytime = 'FIRST_LAST_ANYTIME',
  MultiScore = 'MULTISCORE',
  PickYourSpread = 'PICK_YOUR_SPREAD',
  MovingLineTotals = 'MOVING_LINE_TOTALS',
  OddsBoost = 'ODDS_BOOST'
}

export enum SixPackMarketTemplates {
  PopularSixPack = 'POPULAR_SIX_PACK',
  FirstHalfSixPack = 'FIRST_HALF_SIX_PACK',
  SecondHalfSixPack = 'SECOND_HALF_SIX_PACK',
  FirstQuarterSixPack = 'FIRST_QUARTER_SIX_PACK',
  SecondQuarterSixPack = 'SECOND_QUARTER_SIX_PACK',
  ThirdQuarterSixPack = 'THIRD_QUARTER_SIX_PACK',
  FourthQuarterSixPack = 'FOURTH_QUARTER_SIX_PACK',

  // Live Six Packs
  LiveSixPack = 'LIVE_SIX_PACK',
  LiveFirstHalfSixPack = 'FIRST_HALF_SIX_PACK_LIVE',
  LiveSecondHalfSixPack = 'SECOND_HALF_SIX_PACK_LIVE',
  LiveFirstQuarterSixPack = 'FIRST_QUARTER_SIX_PACK_LIVE',
  LiveSecondQuarterSixPack = 'SECOND_QUARTER_SIX_PACK_LIVE',
  LiveThirdQuarterSixPack = 'THIRD_QUARTER_SIX_PACK_LIVE',
  LiveFourthQuarterSixPack = 'FOURTH_QUARTER_SIX_PACK_LIVE'
}

export interface IAddress {
  line1: string;
  line2: string | null;
  line3: string | null;
  city: string;
  country: string;
  postCode: string;
  timezone: string | null;
  state: string;
}

export interface IDeviceInfo extends DynamicConfiguration {
  address: IAddress;
  brand: Brand;
  deviceId: string;
  cabinetId?: string;
  isSecondaryScreenEnabled: boolean;
  playerCardOperator: PlayerCardOperator;
  playerCardProvider?: string;
  product: 'sports' | 'hotel';
  slotId: ISlotId;
  status: string;
  timezone: string;
  venueName: string;
  globalConfig?: DynamicConfiguration;
  contests: { features: { prizeDraw: { enabled: boolean } } };
  applicationName: string;
}

type FeatureFlag = { enabled: boolean };

interface FeatureFlagWithSports extends FeatureFlag {
  sports: string[];
}

interface FeatureFlagWithRoles extends FeatureFlag {
  roles: string[];
}

export interface BetslipFeatureFlag extends FeatureFlag {
  sports: Record<string, Record<string, string[]>>;
}

export type HubPageSportListFilter = {
  sportId: string;
  name: string;
  primaryMarkets?: {
    preMatch: string[];
  };
  competitions?: string[];
};

type HubPageConfig = {
  enabled: boolean;
  schedulePageConfig: {
    sportsList: HubPageSportListFilter[];
  };
};

type DynamicConfigurationKioskSecondary = {
  incidents: FeatureFlag;
  carousel: {
    isSwipeable: boolean;
  };
  displayMDMBanners: {
    enabled: boolean;
  };
  displayCannedParlayBanners: {
    enabled: boolean;
  };
  bannerLimits: {
    cannedParlays: number | false;
    eventBanners: number | false;
  };
  racebookOnly: FeatureFlag;
};

type DynamicConfigurationPosSecondary = {
  features: {
    cannedParlays: {
      enabled: boolean;
      betslip: BetslipFeatureFlag;
    };
    customerSurvey: {
      enabled: boolean;
    };
  };
};

type DynamicConfigurationServiceMenu = {
  features: {
    kioskMeteringEnabled?: {
      enabled?: boolean;
    };
  };
};

interface FeatureFlagWithWalletConfig extends FeatureFlag {
  pam: FeatureFlag;
  pendingWithdrawals: FeatureFlag;
  pollForWithdrawalStatus: FeatureFlag & { timeoutInMilliseconds: number };
}

// configuration from Dynamic Configuration Service
export type DynamicConfiguration = {
  features: Partial<{
    playerHeadshots: FeatureFlag & {
      competitionsSupported: Array<string>;
    };
    useLogosFromCUI: FeatureFlag & { competitionsSupported: Array<string> };
    publishKioskBalanceChangeAckMessages: FeatureFlag;
    betWithCredits: FeatureFlag;
    racingRewards: {
      compSlipEnabled: boolean;
      ledgerCompCode: string;
      enabled: boolean;
      multiplier: string;
      voucherTypes: {
        label: string;
        value: number;
        launchMoreInfo: boolean;
        category: string;
        subCategory: string;
        ledgerCode: string;
      }[];
    };
    v2MovingLinesTopic: FeatureFlag;
    wagerToWin: FeatureFlag;
    nickelAlignedWagers: FeatureFlag;
    kioskPrimarySecondaryCommunication: FeatureFlag;
    customerFacingScreensBetView: boolean;
    remoteAuthorization: boolean;
    movingLines: FeatureFlagWithSports;
    buyingPointsFixed: FeatureFlagWithSports;
    buyingPointsPYS: FeatureFlagWithSports;
    playerAltLines: FeatureFlagWithSports;
    kioskVoidParimutuel: FeatureFlag & { amount: number };
    canPayout: FeatureFlag;
    cashOut: FeatureFlagWithRoles;
    preventAnonymousBets: FeatureFlag;
    liveScores: {
      enabled: boolean;
      blockedSports?: string[];
    };
    qrCode: FeatureFlag;
    prizeDraw?: FeatureFlag;
    parlayCardsOnKiosk: FeatureFlag;
    livePage: FeatureFlag;
    viewLogos: FeatureFlag;
    superParlay: FeatureFlag;
    reprintTicket: FeatureFlag & { maxReprints: number };
    dualBranding: FeatureFlag;
    rewardsCardTooltip: FeatureFlag;
    oddsBoost: FeatureFlag;
    selfAuth: FeatureFlag;
    exposeDataToChromeExtension: FeatureFlag;
    deviceActivityMonitor: Partial<{ kiosk: { timeoutInSeconds: number } }>;
    schedulePageAutomated: FeatureFlag;
    vaultBalanceCheck: FeatureFlag;
    parlay: Partial<{
      canned: {
        enabled: boolean;
        subscriptionsEnabled: boolean;
        filterAndSortEnabled?: boolean;
        filters?: {
          '20dollarBetWins'?: { text: string; range: [number, number] }[];
        };
        event: FeatureFlag;
        parlayHubQuickPicks: FeatureFlag & {
          eventSearchCount: number;
          automatedQuickPicksPerEventCount: number;
        };
        schedule: FeatureFlag & {
          eventSearchCount: number;
          automatedQuickPicksPerEventCount: number;
        };
        live: FeatureFlag & {
          eventSearchCount: number;
          automatedQuickPicksPerEventCount: number;
        };
        displayLogos?: boolean;
        betslip: BetslipFeatureFlag;
      };
    }>;
    rewards: Partial<{
      caesars: Partial<{
        mobileRegistration: FeatureFlag & {
          codes: Record<'promotion' | 'source', string>;
          password: Record<'instructions' | 'validationRegularExpression', string>;
        };
      }>;
    }>;
    homePageMerchandise: {
      enabled: boolean;
      widgets: {
        events: FeatureFlag;
        oddsBoost: FeatureFlag;
        promoHeader: FeatureFlag;
        cannedParlays: FeatureFlag;
      };
    };
    playerIdentification: {
      dok: FeatureFlag & { inactivityTimeoutInSeconds: number };
      kiosk: FeatureFlag & { inactivityTimeoutInSeconds: number };
      unidentifiedDailyDepositLimit: number;
      facialSimilarityValidityInSeconds: number;
    };
    basketballCompsWithHalves: string[];
    comps: Partial<{
      maxCompsPerDay: number;
      unassigned: FeatureFlag;
      rewards: FeatureFlag;
      betSources: CompsBetSource;
      entitlement: {
        type: string;
        entitlement: string;
        description: string;
        expires: string;
      };
    }>;
    contests?: FeatureFlag &
      Partial<{
        voidContestEntry: FeatureFlag;
      }>;
    diffusionClient: FeatureFlag;
    enhancedSearch: FeatureFlag & {
      eventFilter: FeatureFlag;
      outrightsFilter: FeatureFlag;
      eventStartTimeOffsetHours: number;
      placeholderStrings: string[];
      analytics: FeatureFlag;
      pinnedSearches: FeatureFlag;
      popularSearches: {
        enabled: boolean;
        title: string;
        groups: {
          groupName: string;
          searchTerms: string[];
        }[];
      };
    };
    rewardsCardDisplay: {
      hideNumber?: boolean;
      displayName?: string;
    };
    freeBetWithCoupon: FeatureFlag;
    dailyDepositLimits: {
      dok: { unidentifiedDailyDepositLimit: number };
      kiosk: { unidentifiedDailyDepositLimit: number };
    };
    hubPage: HubPageConfig;
    viewKioskImages: FeatureFlag;
    passwordExpiryReminder: { enabled: boolean; warningWindowInDays: number };
    houseRulesContentsPage: FeatureFlag;
    useNrmd: FeatureFlag;
    logoutConfirmation: FeatureFlag;
    transferOutConfirmation: FeatureFlag;
    writerTransactionHistoryLengthInHours: number;
    consistentNavigation: {
      maxLazyBatchSize: number;
      batchDelay: number;
      oddsBoost: FeatureFlag;
      livePage: FeatureFlag;
      marketDisplayTypeIds: MarketDisplayGroup['marketDisplayTypeId'][];
      sportsSupported: {
        eventPage: string[];
        sportCompetitionPage: string[];
      };
    };
  }>;
  bettingProviders?: {
    sports: FeatureFlag;
    race: FeatureFlag & { version: string; defaultView?: boolean };
  };
  manualRewardEntry?: FeatureFlag;
  betBuilder?: FeatureFlag;
  wallet: FeatureFlagWithWalletConfig;
  oddsBoost: FeatureFlag;
  minStake: number;
  maxStake: number;
  currency: ICurrency;
  sportSchedulePageV2: SportSchedulePageV2FeatureFlag;
  rewardsModal: dynamicConfiguration.RewardsModal;
  parlayCards: ParlayCardsFeatureFlag;
  stateDisplayName: string;
  gamingDayOffset?: string;
  parlayBuilder?: dynamicConfiguration.ParlayBuilder;
  eventsList?: dynamicConfiguration.EventsList;
} & Partial<DynamicConfigurationKioskSecondary> &
  Partial<DynamicConfigurationPosSecondary> &
  Partial<CopConfiguration> &
  Partial<DynamicConfigurationServiceMenu> &
  Partial<ProductContestConfig>;

export type BetSource = 'SPORTSBOOK' | 'PARI_MUTUEL';

export type CompsBetSource = Record<BetSource, { voucherThreshold: number }>;

export type ParlayCardsFeatureFlag = {
  kiosk: FeatureFlag & {
    autoPickWidget: FeatureFlag;
  };
  pos: FeatureFlag & {
    autoPickWidget: FeatureFlag;
  };
};

export type SportSchedulePageV2FeatureFlag = FeatureFlag & {
  scrollingViewportBuffer?: string;
  sports?: SportSchedulePageV2SportsRecord;
};

export type SportSchedulePageV2SportsRecord = Record<string, FeatureFlag & SportsSchedulePageV2SportsBanners>;

export type SportsSchedulePageV2SportsBanners = {
  banners: FeatureFlag;
};

export type RacePointAdjustmentReason = {
  description: string;
  type: 'ADD' | 'REMOVE' | 'ADD_OR_REMOVE';
};

export type CopConfiguration = {
  moduleFederation?: dynamicConfiguration.ModuleFederation;
  sheetPromos?: ISheetPromo[];
  racePointAdjustmentReasons?: RacePointAdjustmentReason[];
  sheetsContentStackFooter?: ContentFooter;
  enablePosFromCop?: FeatureFlag;
  contestListView?: FeatureFlag;
  prizeDraw?: FeatureFlag;
};

export type ProductContestConfig = {
  features: {
    prizeDraw?: FeatureFlag;
  };
};

interface ContentFooter {
  venueId: string;
}

interface ISheetPromo {
  value: string;
  promoUrl: string;
  displayName: string;
}

export interface ICurrency {
  code: string;
  symbol: string;
}

export interface ISlotId {
  venueId: string;
  venueCode: string;
  type: DeviceType;
  slotNumber: number;
  state: StateCode;
}

export enum StateCode {
  Arizona = 'az',
  Central = 'central',
  Colorado = 'co',
  Delaware = 'de',
  WashingtonDC = 'dc',
  Illinois = 'il',
  Indiana = 'in',
  Iowa = 'ia',
  Kansas = 'ks',
  Louisiana = 'la',
  Maine = 'me',
  Massachusetts = 'ma',
  Maryland = 'md',
  Michigan = 'mi',
  Mississippi = 'ms',
  NewJersey = 'nj',
  Nevada = 'nv',
  NorthCarolina = 'nc',
  Ontario = 'ca-on',
  Oregon = 'or',
  Pennsylvania = 'pa',
  PuertoRico = 'pr',
  WashingtonMS = 'wa-ms',
  WashingtonSK = 'wa-sk',
  WestVirginia = 'wv',
  Ohio = 'oh'
}

export enum PeripheralName {
  PRINTER = 'PRINTER',
  SCANNER = 'SCANNER',
  CARD_READER = 'CARD_READER',
  NOTE_ACCEPTOR = 'NOTE_ACCEPTOR'
}

export enum LeagueCode {
  NFL = '007d7c61-07a7-4e18-bb40-15104b6eac92',
  NHL = 'b7b715a9-c7e8-4c47-af0a-77385b525e09',
  MLB = '04f90892-3afa-4e84-acce-5b89f151063d',
  NBA = '5806c896-4eec-4de1-874f-afed93114b8c'
}

export type DeviceType = 'KIOSK' | 'DOK' | 'POS' | 'SAFE';

export type AccountType = 'KIOSK' | 'DOK' | 'CAGE' | 'CAGE_USER' | 'SAFE' | 'WRITER' | 'EXTERNAL_VENUE';

export interface IColumn {
  columns: any;
  className: string;
  id: any;
  player: string;
  status: string;
  currentPriceNum: any;
  currentPriceDen: any;
  onSelect: any;
  currentSelection: any;
  selections: any;
}

export interface IActivity {
  activityType: string;
  count: number;
  value: IBalance;
  creditsValue?: IBalance;
  creditsRedeemed?: number;
}

export interface IBusinessActivitySummary {
  startBalance: IBalance;
  endBalance: IBalance;
  activities: IActivity[];
}

export interface IPageInfo {
  offset: number;
  size: number;
  totalSize: number;
}

export interface IPlaceBets {
  bets: {
    bettingPlatformId?: string;
    id: string;
    stakePerLine: number;
    legs: { id: string; selection: { id: string } }[];
    retailTicketId: string;
  }[];
  hasBetReferral: string;
  referredRetailIds?: string[];
}

export interface IPlaceBetsVariables {
  bets: {
    channel: string;
    eachWay: boolean;
    legs: IParsedLeg[];
    stakePerLine: number;
    type: string;
    requestedPotentialReturns?: number;
    parlayCardEditionId?: string;
    parlayCardEditionTitle?: string;
  }[];
  playerCardId?: string;
}

export interface IPlaceBetsResponse {
  placeBets: IPlaceBets;
}

export interface IOption<TValue = any, TObject = any> {
  label: JSX.Element | string;
  value?: TValue;
  object?: TObject;
}

export interface IGroupedOption<TValue = any, TObject = any> {
  label: JSX.Element | string;
  options: IOption<TValue, TObject>[];
}

export enum ResultType {
  Won = 'won',
  Lost = 'lost',
  Void = 'void',
  Push = 'push',
  Place = 'place',
  Unresulted = '',
  InjuryProtection = 'injury_protection'
}

export enum BetStatusType {
  Ungraded = 'ungraded',
  Payout = 'payout',
  PayoutTaxReportable = 'payoutTaxReportable',
  PayoutTaxWithholdings = 'payoutTaxWithholdings',
  PayoutWithChildSupportCheck = 'payoutWithChildSupportCheck',
  BlockPayout = 'blockPayout',
  BlockPayoutRefund = 'blockPayoutRefund',
  Paid = 'paid',
  PaidViaMailPay = 'paidViaMailPay',
  PushCredits = 'pushCredits',
  Lost = 'lost',
  Void = 'void',
  VoidCashed = 'voidCashed',
  VoidCredits = 'voidCredits',
  Unknown = 'unknown',
  Expired = 'expired',
  Parked = 'parked'
}

export interface IVoucherScannedPayload {
  voucher: { ticketId: string };
}

export type IBuiltBetScannedPayload = {
  data: IBuiltBet;
};

export type VoucherStatus = 'OPEN' | 'LOCKED' | 'REDEEMED' | 'EXPIRED' | 'VOID';

export interface IStoredVoucher {
  ticketId: string;
  value: number;
  validUntil: string;
  createdOn: string;
  createdInVenueCode: string;
  createdOnDeviceId: string;
  createdOnSlotNumber: string;
  lastUpdatedInVenueCode?: string;
  lastUpdatedOnSlotNumber?: string;
  lastUpdatedOnDeviceType?: string;
  lastUpdatedOnDeviceId?: string;
  lastUpdatedByUserId?: string;
  deviceType: string;
  status: VoucherStatus;
  payOutType?: string;
}

export interface StoredMailpay {
  ticketId: string;
  type: string;
  amount: number;
  mailpayDateTime: string;
  issuedDateTime: string;
  issuedVenueId: { venueId: string } | string;
  payoutUserId: string;
}

export interface IVoucher extends Omit<IStoredVoucher, 'value'> {
  value: IMoney;
}

export interface IBalanceChangePayload {
  timestamp: string;
  balances: IBalance[];
}

export interface INoteInsertedPayload {
  timestamp: string;
  value: number;
  currency: string;
}

export interface INoteDepositedPayload {
  timestamp: string;
  value: number;
  currency: string;
}

export interface INoteDepositRejectedPayload {
  timestamp: string;
  value: number;
  currency: string;
  reasons: [
    {
      errorCode: string;
      message: string;
    }
  ];
}

export interface FacialSimilarityPayload {
  timestamp: string;
  reasons: [
    {
      errorCode: string;
      message: string;
    }
  ];
}

export interface IMenuItem {
  id: string;
  label: string;
  disabled?: boolean;
}

export interface IFormState {
  errors: any;
  values: any;
  invalid: boolean;
}

export interface ILoginCredentials {
  username: string;
  password: string;
}

export interface IBetScannedPayload {
  timestamp?: string;
  bet: IPlacedBet;
}

export interface ILink {
  to: string | (() => void);
  text: string;
}

export interface IBackLink extends Omit<ILink, 'to'> {
  to?: string;
  heading?: string;
}

export interface IBreadcrumb {
  title: string;
  links: ILink[];
}

export type BreadcrumbTitle = {
  heading: string;
  titles?: string[];
};

export enum LimitStatus {
  error = 'ERROR',
  warning = 'WARNING',
  ok = 'OK'
}
export interface ITally {
  id: string;
  cashTally: number;
  payoutTally: number;
  slotNumber: string;
  limitStatus: LimitStatus;
  venue: {
    name: string;
  };
}

export interface IPageInfo {
  offset: number;
  size: number;
  totalSize: number;
  hasNext?: boolean;
}

export interface IPagedResults<T> {
  content: T[];
  pageInfo: IPageInfo;
}

export enum ActivityType {
  ADD_CASH = 'addCash',
  REMOVE_CASH = 'removeCash',
  BET_PLACED = 'BET_PLACED',
  BET_PLACED_REWARDS = 'BET_PLACED_REWARDS',
  BET_PLACED_PARIMUTUEL = 'BET_PLACED_PARIMUTUEL',
  BET_PAIDOUT_PARIMUTUEL = 'BET_PAIDOUT_PARIMUTUEL',
  BET_REFUNDED_PARIMUTUEL = 'BET_REFUNDED_PARIMUTUEL',
  BET_PAIDOUT = 'BET_PAIDOUT',
  VOUCHER_CREATED = 'VOUCHER_CREATED',
  VOUCHER_PAIDOUT = 'VOUCHER_PAIDOUT',
  WALLET_DEPOSIT = 'WALLET_DEPOSIT',
  TRANSFER_IN = 'TRANSFER_IN',
  CONTEST_ENTRY = 'CONTEST_ENTRY',
  CONTEST_PRIZE_PAIDOUT = 'CONTEST_PRIZE_PAIDOUT',
  BONUS_PRIZE_PAIDOUT = 'BONUS_PRIZE_PAIDOUT',
  ROUND_PRIZE_PAIDOUT = 'ROUND_PRIZE_PAIDOUT'
}

export interface ICashAdjustmentForm {
  activity: keyof typeof ActivityType;
  amount: string;
  reason: string;
  venueId: string;
}

export interface IPlayerWallet {
  name: string;
  email: string;
  cellPhone: string;
  dob: string;
  playerId: string;
  status: string;
  account: {
    realMoneyBalance: number;
    bonusMoneyBalance: number;
  };
  username?: string;
}

export type WalletAction = 'deposit' | 'withdrawal';

type BalancesSuccessResponse = {
  Success: {
    total: number;
    withdrawable: number;
  };
};

type TransferInProgressResponse = {
  TransferInProgress: {
    retryIn: number;
  };
};

export type CustomerBalance = BalancesSuccessResponse | TransferInProgressResponse;

export const isSuccessResponse = (balances: CustomerBalance): balances is BalancesSuccessResponse =>
  'Success' in balances;

export const isTransferInProgressResponse = (balances: CustomerBalance): balances is TransferInProgressResponse =>
  'TransferInProgress' in balances;

export interface IMarketGrouping {
  id: string;
  name: string;
}

interface IPrint {
  timestamp: string;
}

export type CashOutAvailabilityReason =
  | 'ANONYMOUS_BET'
  | 'NOT_AVAILABLE_FOR_BET'
  | 'UNSUPPORTED_REWARDS'
  | 'NOT_PERMITTED';

export interface CashOutExtension {
  enabled: boolean;
  available: boolean;
  availabilityReason: CashOutAvailabilityReason | null;
  amount: number;
}

export interface RewardScheme {
  operator: string;
  provider: string;
  providerPrefix: string;
}

export interface IVenue {
  id: string;
  venueId: string;
  venueCode: string;
  name: string;
  displayName: string;
  address: IAddress;
  rewardScheme?: RewardScheme;
  isDokOnlyVenue: boolean;
  usageType: string;
  branding: string;
}

export type DeviceStatus =
  | 'INSTALL_PENDING'
  | 'READY'
  | 'MAINTENANCE'
  | 'OUT_OF_SERVICE'
  | 'OFF_LINE'
  | 'LOCKED'
  | 'DECOMMISSIONED'
  | 'UNPLAYABLE';

export interface IPeripheralMessagePayload extends SubPayload {
  deviceId: string;
  disabled: boolean;
  locked: boolean;
  lockReason: string | null;
  status: DeviceStatus;
}

export type LiveScoreSports = 'americanfootball' | 'basketball' | 'baseball' | 'icehockey' | 'football' | 'tennis';

export type ScoreBase = {
  team: string;
};

export type PointsScore = ScoreBase & {
  points: number;
};

export type GoalsScore = ScoreBase & {
  goals: number;
};

export type RunsScore = ScoreBase & {
  runs: number;
};

export type TennisScore = ScoreBase & {
  gamePoints: string;
  setPoints: {
    set: number;
    points: number;
  }[];
  matchPoints: number;
};

export type Score = PointsScore | GoalsScore | RunsScore | TennisScore;

export const isPointsScore = (score: Score): score is PointsScore => 'points' in score;
export const isGoalsScore = (score: Score): score is GoalsScore => 'goals' in score;
export const isRunsScore = (score: Score): score is RunsScore => 'runs' in score;
export const isTennisScore = (score: Score): score is TennisScore => 'setPoints' in score;

export type SportScore<SportId extends LiveScoreSports> = SportId extends 'americanfootball'
  ? PointsScore
  : SportId extends 'basketball'
    ? PointsScore
    : SportId extends 'baseball'
      ? RunsScore
      : SportId extends 'icehockey'
        ? GoalsScore
        : SportId extends 'football'
          ? GoalsScore
          : SportId extends 'tennis'
            ? TennisScore
            : never;

type LiveScoreBase<SportId extends LiveScoreSports> = {
  __typename?: string;
  sportName: SportId;
  eventId: string;
  statsId?: number;
  startTime: string;
  scores: SportScore<SportId>[];
};

export type LiveScoreGameTime = {
  minutes: number;
  seconds: number;
};

export type FootballLiveScore = LiveScoreBase<'football'> & {
  gameTime: LiveScoreGameTime;
  period: number;
  strPeriod: string;
};

export type AmericanFootballLiveScore = LiveScoreBase<'americanfootball'> & {
  gameTime: LiveScoreGameTime;
  quarter: number;
};

export type BasketballLiveScore = LiveScoreBase<'basketball'> & {
  gameTime: LiveScoreGameTime;
  period: number;
};

export type BaseballLiveScore = LiveScoreBase<'baseball'> & {
  inning: {
    number: number;
    half: string;
  };
};

export type HockeyLiveScore = LiveScoreBase<'icehockey'> & {
  gameTime: LiveScoreGameTime;
  period: number;
};

export type TennisLiveScore = LiveScoreBase<'tennis'> & {
  server: string;
  setsPerMatch: number;
  currentGame: number;
  currentSet: number;
  postMatch: boolean;
};

export type LiveScore =
  | BaseballLiveScore
  | BasketballLiveScore
  | HockeyLiveScore
  | AmericanFootballLiveScore
  | FootballLiveScore
  | TennisLiveScore;

export const isAmericanFootballLiveScore = (liveScore: LiveScore): liveScore is AmericanFootballLiveScore =>
  liveScore.sportName === SportIds.AMERICAN_FOOTBALL;
export const isBasketballLiveScore = (liveScore: LiveScore): liveScore is BasketballLiveScore =>
  liveScore.sportName === SportIds.BASKETBALL;
export const isBaseballLiveScore = (liveScore: LiveScore): liveScore is BaseballLiveScore =>
  liveScore.sportName === SportIds.BASEBALL;
export const isHockeyLiveScore = (liveScore: LiveScore): liveScore is HockeyLiveScore =>
  liveScore.sportName === SportIds.ICE_HOCKEY;
export const isFootballLiveScore = (liveScore: LiveScore): liveScore is FootballLiveScore =>
  liveScore.sportName === SportIds.FOOTBALL;
export const isTennisLiveScore = (liveScore: LiveScore): liveScore is TennisLiveScore =>
  liveScore.sportName === SportIds.TENNIS;

export interface ICashAccepted {
  accountId: string;
  count: number;
  lastCleared: string;
  maxCount: number;
}

export const SportIds = {
  AMERICAN_FOOTBALL: 'americanfootball',
  AUSTRALIAN_RULES: 'australianrules',
  BASEBALL: 'baseball',
  BASKETBALL: 'basketball',
  BOXING: 'boxing',
  CRICKET: 'cricket',
  DARTS: 'darts',
  FOOTBALL: 'football',
  GOLF: 'golf',
  HANDBALL: 'handball',
  ICE_HOCKEY: 'icehockey',
  RUGBY_UNION: 'rugbyunion',
  TABLE_TENNIS: 'tabletennis',
  TENNIS: 'tennis'
};

export type PropsFrom<T extends (...args: any) => any> = Parameters<T>[0];
